import React, { useState, useRef, useEffect, useCallback } from "react";
import { createBrowserHistory } from "history";
import { isDevelopment } from "./isDevelopment";
import { BffHelper, TokenData } from "./BffHelper";
import { UsernameAndPassword } from "./UsernameAndPassword";
import { DebugComponent } from "./DebugComponent";
import { LaunchComponent } from "./LaunchComponent";
import { IressButton, IressForm, IressProgress, useToaster } from "@iress-oss/ids-components";

enum SpaState {
  Waiting, // processing taking place, no user input required (this is also the initial state)
  RequestCredentials, // request credentials from the user
  CredentialsAccepted, // user credentials accepted
  Launched, // O&M Profiler has been launched.
}

export const OandMComponent: React.FC = () => {
  const [state, setState] = useState<SpaState>(SpaState.Waiting);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState<string>("");
  const [ssoToken, setSsoToken] = useState<string>("");
  const [processingMessage, setProcessingMessage] = useState<string | null>("Loading...");
  const [tokenLoadComplete, setTokenLoadComplete] = useState<boolean>(false);

  // Debug: Log state changes
  useEffect(() => {
    console.log(`State changed to: ${state} (${SpaState[state]})`);
  }, [state]);

  const bffHelperRef = useRef<BffHelper | null>(null);
  const passwordRef = useRef<string>("");
  const clientIdRef = useRef<string>("");
  const oAndMUrlRef = useRef<URL | null>(null);
  const modifierKeysRef = useRef({ ctrlKey: false, shiftKey: false });
  const toaster = useToaster();

  // Track modifier keys throughout component lifecycle
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      modifierKeysRef.current.ctrlKey = event.ctrlKey;
      modifierKeysRef.current.shiftKey = event.shiftKey;
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      modifierKeysRef.current.ctrlKey = event.ctrlKey;
      modifierKeysRef.current.shiftKey = event.shiftKey;
    };

    const handleBlur = () => {
      // Reset modifier keys when window loses focus
      modifierKeysRef.current.ctrlKey = false;
      modifierKeysRef.current.shiftKey = false;
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const setupBffHelper = useCallback((): void => {

  // Get configuration data from Xplan
    let deployConfig = window.deployConfig;

    if (isDevelopment()) {
      console.log("isDevelopment == true");
      deployConfig = { bffURL: "http://localhost:9000"};
    }

    console.log("Setting up BFF Helper...");
    console.log("Current location:", window.location.href);

    if (!deployConfig) {
      console.error("deployConfig is not available on window object");
      setError("Application configuration is missing. Please contact support.");
      return;
    }

    if (!deployConfig.bffURL) {
      console.error("deployConfig.bffURL is not set");
      setError("BFF URL configuration is missing. Please contact support.");
      return;
    }

    console.log(`BFF URL (from deployConfig): ${deployConfig.bffURL}`);

    try {
      bffHelperRef.current = new BffHelper(
        "setupBffHelper",
        deployConfig.bffURL
      );
      console.log("BFF Helper successfully created");
    } catch (error) {
      console.error("Error creating BFF Helper:", error);
      setError(`Failed to initialize BFF Helper: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, []);

  const getStoredSSOToken = useCallback(async () => {
    console.log("GetStoredSSOToken");

    if (bffHelperRef.current === null) {
      setProcessingMessage(null);
      setSsoToken("");
      setError("bffHelper === null");
      return;
    }

    const tokenData: TokenData = await bffHelperRef.current.loadSSOToken();

    setProcessingMessage(null);
    setUsername(tokenData.username);
    setSsoToken(tokenData.ssoToken);
    setError(null);
    setTokenLoadComplete(true);
  }, []);

  const postLaunch = useCallback(async (): Promise<void> => {
    setState(SpaState.Launched);
    setProcessingMessage(null);

    console.log("Go Back to Xplan");
    
    // Check if both CTRL and SHIFT keys are currently held down
    const isCtrlShiftHeld = modifierKeysRef.current.ctrlKey && modifierKeysRef.current.shiftKey;
    
    if (isCtrlShiftHeld) {
      console.log("Skipped browser back navigation - CTRL+SHIFT held down");
    } else {
      createBrowserHistory().back();
    }
  }, []);

  const launchOAndM = useCallback((): void => {
    console.log("Launch O&M Profiler");

    const formId: string = "om-profiler-post-data-form";

    console.log(`Form Id = ${formId}`);

    const form: HTMLFormElement | null = document.querySelector("#" + formId);

    if (form === null) {
      console.log("(form === null)");
    } else {
      const submitButtonId: string = "submit-button-to-launch-o-and-m-profiler";

      console.log(`Submit button Id = ${submitButtonId}`);

      const submitButton = form.querySelector("#" + submitButtonId);

      if (submitButton) {
        console.log("requestSubmit");
        form.requestSubmit(submitButton as HTMLElement);
      } else {
        console.log("Unable to find submit button");
        form.requestSubmit();
      }
    }
  }, []);

  const startup = useCallback(async (bffHelper: BffHelper): Promise<void> => {
    try {
      console.log("Attempting to retrieve O&M Profiler URL from BFF...");
      const profilerUrl = await bffHelper.getProfilerUrl();

      if (profilerUrl === null) {
        console.error("BFF returned null for profiler URL");
        setError("Unable to retrieve O&M Profiler Url from server. Please contact support.");
        return;
      }

      oAndMUrlRef.current = profilerUrl;
      console.log(`O&M Profiler url successfully retrieved: ${profilerUrl.toString()}`);

      const loadingMessage = "Loading stored SSO token from Xplan...";
      console.log(loadingMessage);

      await getStoredSSOToken();

      // Note: We need to get the current values after getStoredSSOToken() completes
      // We'll use a callback approach to get the latest values
    } catch (error) {
      console.error("Error in startup function:", error);
      setError(`Failed to initialize application: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [getStoredSSOToken]);

  // Separate effect to handle token validation after username/ssoToken are updated
  useEffect(() => {
    const validateTokenAndLaunch = async () => {
      console.log(`validateTokenAndLaunch: checking conditions - bffHelper exists: ${!!bffHelperRef.current}, state: ${state}, tokenLoadComplete: ${tokenLoadComplete}`);
      if (!bffHelperRef.current || state !== SpaState.Waiting || !tokenLoadComplete) return;

      let isTokenValid: boolean = false;

      if (
        username !== null &&
        username.trim() !== "" &&
        ssoToken !== null &&
        ssoToken.trim() !== ""
      ) {
        const checkingMessage = "Checking if SSO token is valid...";
        console.log(checkingMessage);

        isTokenValid = await bffHelperRef.current.checkSSOToken(ssoToken);
      } else {
        console.log("Skipped token check - missing username or ssoToken");
        console.log(`Username "${username}"`);
        console.log(`SSO token "${ssoToken}"`);
      }

      console.log(`isTokenValid ${isTokenValid}`);

      if (isTokenValid) {
        setProcessingMessage("Launching O&M Profiler");

        // Launch
        launchOAndM();

        await postLaunch();
      } else {
        // Display username and password
        console.log("Setting state to RequestCredentials - username/password form should now display");
        setState(SpaState.RequestCredentials);
        setProcessingMessage(null);
      }
    };

    // Run validation when token loading is complete and state is Waiting
    if (tokenLoadComplete && state === SpaState.Waiting) {
      validateTokenAndLaunch();
    }
  }, [tokenLoadComplete, username, ssoToken, state, launchOAndM, postLaunch]);

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        // Get the Client ID from the URL.
        clientIdRef.current =
          new URLSearchParams(new URL(document.URL).search).get("entityid") ?? "";
        if (clientIdRef.current.trim() === "") {
          console.log(
            'You must launch the SPA with a client Id. This should be in the form "?entityid=5972".'
          );
          return;
        }

        setupBffHelper();

        if (bffHelperRef.current) {
          await startup(bffHelperRef.current);
        }
      } catch (error) {
        console.error("Error in componentDidMount:", error);
        setError("An unexpected error occurred during startup.");
      }
    };

    initializeComponent();
  }, [setupBffHelper, startup]);

  // Validates the user input.
  // If input is valid this will return null, else it will return a message describing the problem.
  const validateInput = useCallback((): string | null => {
    if (!username || username.trim() === "") {
      return 'You must enter a value for "O&M Profiler sign in".\r\nThis will be an e-mail address.';
    }

    return null;
  }, [username]);

  const onLaunchOandM = useCallback(async (): Promise<void> => {
    console.log(`onLaunchOandM`);

    const validationMessage = validateInput();

    if (validationMessage !== null) {
      console.log(`Validation message ${validationMessage}`);
      toaster.error({
          heading: 'Validation Error',
          content:
            validationMessage
        });
      return;
    }

    if (!bffHelperRef.current) {
      console.log("BffHelper === null!!!");
      setError("System initialization error. Please refresh the page.");
      return;
    }

    try {
      const requestMessage = "Requesting new SSO token...";
      console.log(requestMessage);

      setState(SpaState.CredentialsAccepted);

      const newSsoToken = await bffHelperRef.current.requestSSOTokenFromOandM(
        username,
        passwordRef.current
      );

      console.log(`New SSO token ${newSsoToken}`);

      if (newSsoToken === null || newSsoToken.trim() === "") {
        setState(SpaState.RequestCredentials);
        setProcessingMessage(null);

        const errorMessage =
          "Unable to login to O&M Profiler, please check your username and password.";
        console.log(errorMessage);
        toaster.error({
            heading: 'Error',
          content:
              errorMessage
          });
        return;
      }

      const savingMessage = "Saving new SSO token back to Xplan...";

      console.log(savingMessage);
      setState(SpaState.CredentialsAccepted);
      setSsoToken(newSsoToken);

      await bffHelperRef.current.saveSSOToken(
        newSsoToken,
        username
      );

      launchOAndM();
      await postLaunch();
    } catch (error) {
      console.error("Error during SSO token request:", error);
      setState(SpaState.RequestCredentials);
      setProcessingMessage(null);
      setError("Failed to authenticate. Please try again.");
    }
  }, [username, launchOAndM, postLaunch, validateInput, toaster]);

  const onUsernameChanged = (newUsername: string): void => {
    console.log(`Username: ${newUsername}`);
    setUsername(newUsername);
  };

  const onPasswordChanged = useCallback((password: string): void => {
    passwordRef.current = password;
  }, []);

  // Render logic
  const deployConfig = window.deployConfig;

  // Debugger Div (hidden by default)
  const debug = (
    <div hidden>
      <h1>Debugger</h1>
      {deployConfig?.bffURL && (
        <DebugComponent
          bffHelper={
            new BffHelper(
              "Debugger",
              deployConfig.bffURL
            )
          }
          clientId={clientIdRef.current}
          OmProfilerUrl={oAndMUrlRef.current}
        />
      )}
    </div>
  );

  if (error) {
    return <div className="App-progress-message App-progress-message--error">Error: {error}</div>;
  }

  const progressInstance = (
    <div>
      <div className="App-progress-message">
        <div>
          <div>
            <LaunchComponent
              clientId={clientIdRef.current}
              oAndMUrl={oAndMUrlRef.current}
              username={username}
              ssoToken={ssoToken}
            />
          </div>
          <p className="App-progress-message">{processingMessage}</p>
        </div>
        <IressProgress />
      </div>
    </div>
  );

  if (state === SpaState.Waiting) {
    return progressInstance;
  }
  if (state === SpaState.Launched) {
    return (
      <div className="ProgressPanel">
        {debug}
        <div className="App-progress-message">
          Returning to Xplan client...
        </div>
      </div>
    );
  }

  return (
    <div>
      {debug}
      <div>
        <LaunchComponent
          clientId={clientIdRef.current}
          oAndMUrl={oAndMUrlRef.current}
          username={username}
          ssoToken={ssoToken}
        />
      </div>
      <IressForm id="fieldset-username-password">
        <div>
          <UsernameAndPassword
            username={username}
            onUsernameChanged={onUsernameChanged}
            onPasswordChanged={onPasswordChanged}
            disabled={state === SpaState.CredentialsAccepted}
          />

          {/* Launch button panel */}
          <div className="Button">
            <IressButton
              id="button-launch-profiler"
              onClick={() => onLaunchOandM()}
              mode="primary"
              className={state === SpaState.CredentialsAccepted ? 'disabled' : ''}
              disabled={state === SpaState.CredentialsAccepted}
            >
              Launch O&amp;M Profiler
            </IressButton>
            {state === SpaState.CredentialsAccepted && progressInstance}
          </div>
          <div className="Sign-Up">
            No login for O&M Profiler?
            Click <a href="https://www.iress.com/omprofiler">here</a> to find out more
          </div>
        </div>

        {/* SSO token panel & client ID - for development only */}
        <div className="Section-header">
          <div id="user_inputs" className="hidden-inputs">
            <input
              type="hidden"
              className="ssoToken"
              value={ssoToken}
              id="token"
              name="token"
            />
            <input
              type="hidden"
              id="XplanClientId"
              name="XplanClientId"
              value={clientIdRef.current}
            />
          </div>
        </div>
      </IressForm>
    </div>
  );
};